// The portal security questions of the client application (Part 11 of the
// individual form, and a part of each entity form).
//
// The form prints seven questions, and the client answers four of them. A row
// alone is therefore optional, and a form rule counts the answers. This
// module imports no other module of the bank, so the bank and the form rules
// can both import it.

import type { ClientApplicationFormRule } from './client-application-questions';

/** The seven questions the form prints in Part 11. The client chooses four. */
export const PORTAL_SECURITY_ROWS: ReadonlyArray<{ key: string; label: string }> = [
    { key: 'first_car', label: 'Make of first car' },
    { key: 'holiday_destination', label: 'Favourite holiday destination' },
    { key: 'first_pet', label: 'Name of your first pet' },
    { key: 'childhood_road', label: 'Name of the road you grew up in' },
    { key: 'favourite_colour', label: 'Favourite colour' },
    { key: 'mothers_maiden_name', label: "Mother's maiden name" },
    { key: 'fathers_first_name', label: "Father's first name" },
];

/** How many of the seven portal security answers a client must give. */
export const CLIENT_APPLICATION_PORTAL_SECURITY_ANSWERS_REQUIRED = 4;

/** The ids of the seven portal security answers, in the order the form prints them. */
export const CLIENT_APPLICATION_PORTAL_SECURITY_QUESTION_IDS: readonly string[] =
    PORTAL_SECURITY_ROWS.map((row) => `portal_security_answer_${row.key}`);

/** The message under the first blank portal security answer. */
export const CLIENT_APPLICATION_PORTAL_SECURITY_MESSAGE = `Answer ${CLIENT_APPLICATION_PORTAL_SECURITY_ANSWERS_REQUIRED} of these questions.`;

/**
 * The client answers four of the seven portal security questions. The rule
 * checks only when the form must be complete, and it puts its error under the
 * first blank answer. A form with no portal section gives no error.
 */
export const CLIENT_APPLICATION_PORTAL_SECURITY_RULE: ClientApplicationFormRule = {
    id: 'portal_security_answers',
    check: (answers, context) => {
        if (!context.requireComplete) return [];
        const blank = CLIENT_APPLICATION_PORTAL_SECURITY_QUESTION_IDS.filter((id) => {
            const answer = answers[id];
            return typeof answer !== 'string' || answer.trim() === '';
        });
        const given = CLIENT_APPLICATION_PORTAL_SECURITY_QUESTION_IDS.length - blank.length;
        if (given >= CLIENT_APPLICATION_PORTAL_SECURITY_ANSWERS_REQUIRED) return [];
        const first = blank[0];
        return first === undefined
            ? []
            : [{ questionId: first, message: CLIENT_APPLICATION_PORTAL_SECURITY_MESSAGE }];
    },
};
